import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(sessionId ? "polling" : "missing");
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (attempts >= 20) { setStatus("timeout"); return; }
        setAttempts((a) => a + 1);
        timerRef.current = setTimeout(poll, 2000);
      } catch { if (!cancelled) { setStatus("error"); } }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="sect-navy pt-32 pb-24 min-h-screen">
      <div className="bcu-container max-w-3xl">
        {status === "paid" && (<>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#78C800]/40 bg-[#78C800]/10 text-[#78C800] text-[11px] tracking-[0.22em] uppercase rounded font-bold"><CheckCircle2 className="h-4 w-4" /> Payment confirmed</div>
          <h1 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-display leading-[1.02]">Welcome to <span className="text-[#F7931E]">Bounce City University.</span></h1>
          <p className="mt-6 text-[#C7CFDD]">Your enrollment payment has been received. Onboarding instructions will be sent to your email shortly.</p>
        </>)}
        {status === "polling" && (<div className="flex items-center gap-3 text-[#C7CFDD]"><Loader2 className="h-5 w-5 animate-spin text-[#F7931E]" /> Confirming your payment…</div>)}
        {status === "timeout" && (<>
          <h1 className="text-3xl md:text-5xl font-display">Payment confirmation is taking longer than expected.</h1>
          <p className="mt-4 text-[#C7CFDD]">Please refresh in a moment or contact enrollment support.</p>
        </>)}
        {status === "missing" && (<>
          <h1 className="text-3xl md:text-5xl font-display">Missing payment session.</h1>
          <p className="mt-4 text-[#C7CFDD]">This page requires a Stripe checkout session identifier.</p>
        </>)}
        {status === "error" && (<>
          <h1 className="text-3xl md:text-5xl font-display">We could not confirm your payment.</h1>
          <p className="mt-4 text-[#C7CFDD]">Please contact enrollment support with your session reference.</p>
        </>)}
        <div className="mt-10">
          <Link to="/" className="bcu-btn-primary">Return to home <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </div>
  );
}
